import React from 'react';
import { ArrowRightIcon, HomeIcon, FileTextIcon, CreditCardIcon, CheckIcon, MapPinIcon, ShieldCheckIcon, InfoIcon } from 'lucide-react';
import { PERMIS_IMAGES, questionCopy, processSteps, propertyCopy, verifiedCopy, type Bi } from '../../data/permisHero';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
export const CARD = 'rounded-2xl border border-[#ECECF4] bg-white shadow-[0_18px_40px_-26px_rgba(40,36,120,0.45)]';
export function GreenCheck({
  size = 18
}: {
  size?: number;
}) {
  return <span className="rounded-full bg-[#22A04F] flex items-center justify-center shrink-0" style={{
    width: size,
    height: size
  }} aria-hidden="true" data-id="element-4860" data-mp="2ztt4p6c7oecj">
      <CheckIcon className="text-white" style={{
      width: size * 0.6,
      height: size * 0.6
    }} strokeWidth={3} data-id="element-4861" data-mp="2qsva1ennxj6m" />
    </span>;
}
export function QuestionCard({
  isFR
}: P) {
  return <div className={`${CARD} rounded-[22px] p-3 pr-4 flex items-center gap-4`} data-id="element-4862" data-mp="1ka6kyax3uglu">
      <img src={PERMIS_IMAGES.avatar} alt="" className="w-[64px] h-[64px] rounded-full object-cover shrink-0" data-id="element-4863" data-mp="1fc53fufbupch" />
      <p className="flex-1 font-sans text-[14.5px] font-medium text-[#121A3A] leading-snug" data-id="element-4864" data-mp="6swy47eqqrug">{pk(isFR)(questionCopy)}</p>
      <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4865" data-mp="3tzpk3oem2bqv">
        <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2.2} data-id="element-4866" data-mp="2sd0l2q99m7s" />
      </span>
    </div>;
}
const STEP_ICONS = {
  home: HomeIcon,
  doc: FileTextIcon,
  pay: CreditCardIcon
} as const;
export function ProcessSteps({
  isFR
}: P) {
  const pick = pk(isFR);
  return <ol className="relative space-y-4" data-id="element-4867" data-mp="3b8y0lu0wt4yr">
      <span className="absolute left-[38px] top-2 bottom-6 w-[3px] rounded-full bg-[#4F46E5]/80" aria-hidden="true" data-id="element-4868" data-mp="3ba96bzdx0msi" />
      {processSteps.map(s => {
      const Icon = STEP_ICONS[s.key];
      return <li key={s.key} className={`${CARD} relative rounded-[20px] px-3 py-3 flex items-center gap-3.5`} data-id="element-4869" data-mp="31ba8zcylodkt">
            <span className="w-[52px] h-[52px] rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4870" data-mp="3693avtsvelfa">
              <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} data-id="element-4871" data-mp="1736f0lksecfx" />
            </span>
            <span className="min-w-0" data-id="element-4872" data-mp="131ci8odppsl1">
              <span className="flex items-center gap-2 font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-4873" data-mp="132e31kkj5tfy">
                {pick(s.title)}
                <GreenCheck size={17} data-id="element-4874" data-mp="y05b8rje8xff" />
              </span>
              <span className="block font-sans text-[11.5px] text-[#5B6478] mt-0.5" data-id="element-4875" data-mp="1ur7hbcrdgwmw">{pick(s.sub)}</span>
            </span>
          </li>;
    })}
    </ol>;
}
export function PropertyCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} rounded-[22px] p-3.5 flex flex-col sm:flex-row gap-4`} data-id="element-4876" data-mp="1ltnfo2plkybr">
      <div className="relative w-full sm:w-[160px] h-[130px] rounded-xl overflow-hidden shrink-0" data-id="element-4877" data-mp="1gutxvy3a2l5s">
        <img src={PERMIS_IMAGES.parcel} alt="" className="absolute inset-0 w-full h-full object-cover saturate-[0.8]" data-id="element-4878" data-mp="aakoryo17i1e" />
        <svg viewBox="0 0 160 130" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true" data-id="element-4879" data-mp="3rf1oxm4s8jh6">
          <g stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="0.8" fill="none" data-id="element-4880" data-mp="7xiotggp19t">
            <path d="M0 40 L160 30 M0 88 L160 80 M50 0 L42 130 M108 0 L114 130" data-id="element-4881" data-mp="583coga7ezv2" />
          </g>
          <polygon points="56,46 104,40 108,84 60,90" fill="#6D5DF6" fillOpacity="0.45" stroke="#FFFFFF" strokeWidth="1.5" data-id="element-4882" data-mp="9ya0idww6gc9" />
        </svg>
        <span className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-full w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center" aria-hidden="true" data-id="element-4883" data-mp="dzc04vuq0uer">
          <MapPinIcon className="w-5 h-5 text-[#4F46E5] fill-[#4F46E5]/15" strokeWidth={2.2} data-id="element-4884" data-mp="ny1liyltswzr" />
        </span>
      </div>
      <ul className="flex-1 flex flex-col justify-center gap-2.5 min-w-0" data-id="element-4885" data-mp="sw35rmuzn2ct">
        {propertyCopy.items.map((it, i) => <li key={i} className="flex items-center justify-between gap-3" data-id="element-4886" data-mp="2prr3pnilwr2j" data-mpi={i}>
            <span className={`font-sans text-[12.5px] ${i === 0 ? 'font-semibold text-[#121A3A]' : 'text-[#5B6478]'}`} data-id="element-4887" data-mp="2upkb2jvnbr4e">{pick(it)}</span>
            <GreenCheck size={20} data-id="element-4888" data-mp="p2bi4hk7p3q5" />
          </li>)}
      </ul>
    </div>;
}
export function VerifiedCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className={`${CARD} rounded-[22px] px-4 py-3.5 w-full max-w-[330px]`} data-id="element-4889" data-mp="y0n2w2yed7ao">
      <div className="flex items-start gap-3" data-id="element-4890" data-mp="t353p4a6m6fv">
        <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-4891" data-mp="t096ji65rlsh">
          <ShieldCheckIcon className="w-5 h-5 text-white fill-[#4F46E5]" strokeWidth={2} data-id="element-4892" data-mp="1b5xzq78npa1" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-4893" data-mp="f2udm3gp4vvr">
          <p className="flex items-center justify-between gap-2 font-sans text-[14px] font-semibold text-[#121A3A]" data-id="element-4894" data-mp="f3vmq2e5j8au">
            {pick(verifiedCopy.title)}
            <InfoIcon className="w-4 h-4 text-[#9A9FB5]" aria-hidden="true" data-id="element-4895" data-mp="3iqvy3s93r9fa" />
          </p>
          <p className="font-sans text-[11.5px] text-[#5B6478] mt-1" data-id="element-4896" data-mp="3dsknhfazguw0">{pick(verifiedCopy.line1)}</p>
          <p className="font-sans text-[11.5px] text-[#5B6478]" data-id="element-4897" data-mp="1taikbngwqq9q">{pick(verifiedCopy.line2)}</p>
        </div>
      </div>
      <p className="mt-2.5 flex items-center gap-2 font-sans text-[11.5px] text-[#3A4050]" data-id="element-4898" data-mp="1y18nqv5rwj1s">
        <span className="flex gap-1" aria-hidden="true" data-id="element-4899" data-mp="1kaddbefmltw3">
          {[0, 1, 2, 3, 4].map(i => <span key={i} className={`w-2.5 h-2.5 rounded-full ${i < verifiedCopy.score ? 'bg-[#22A04F]' : 'bg-[#DCE7DF]'}`} data-id="element-4900" data-mp="1p7vpddoxwp8b" />)}
        </span>
        {pick(verifiedCopy.confidence)}
      </p>
    </div>;
}